import './styles.scss'
import { useState } from 'react'
import UnbeatableSection from '../../Components/UnbeatableSection'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid, brands } from '@fortawesome/fontawesome-svg-core/import.macro'
import Input from '../../Components/Input'
import InputArea from '../../Components/InputArea'
import CustomButton from '../../Components/CustomButton'
import { useForm } from '../../Hooks/useForm'
import emailjs from '@emailjs/browser'

const templateId = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID
const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID
const publicId = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const MSG_LIMIT = 500

const Req = () => <span className="req">*</span>

const ContactUs = () => {
  const [status, setStatus] = useState(null) // null | 'success' | 'error'

  const { form, onChange, reset } = useForm({
    name: '',
    company: '',
    email: '',
    phone: '',
    inquiry_type: '',
    country: '',
    subject: '',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => onChange(e.target.name, e.target.value)

  const handleSubmit = async (e) => {
    e.preventDefault()
    onChange('loading', true)
    setStatus(null)

    try {
      await emailjs.send(serviceId, templateId, form, publicId)
      reset()
      setStatus('success')
    } catch (error) {
      setStatus('error')
    }

    onChange('loading', false)
  }

  return (
    <div className="contact-us">
      <div className="contact-us__top">
        <h1>Contact Us</h1>
        <p>Our team is available to assist with any logistics inquiry — reach out and we'll respond within 24 hours.</p>
      </div>

      <div className="contact-us__content">
        <section className="contact-us__content__details">
          <div className="contact-us__content__details__info">
            <h2>Get In Touch</h2>
            <p>
              Sterling Logistics has been delivering reliable shipping and
              courier solutions since 1992. Whether you need a quote, have a
              question about an active shipment, or want to explore a long-term
              partnership — we're here to help.
            </p>

            <div className="contact-item">
              <div className="contact-item__icon">
                <FontAwesomeIcon icon={solid('phone')} />
              </div>
              <div className="contact-item__text">
                <span className="contact-item__label">Phone</span>
                <span>+1 (512) 643-7634</span>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item__icon">
                <FontAwesomeIcon icon={solid('envelope-open')} />
              </div>
              <div className="contact-item__text">
                <span className="contact-item__label">Email</span>
                <span>business@sterlinglogistics.us</span>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item__icon">
                <FontAwesomeIcon icon={solid('map-location-dot')} />
              </div>
              <div className="contact-item__text">
                <span className="contact-item__label">Location</span>
                <span>Global Facility Office</span>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item__icon">
                <FontAwesomeIcon icon={solid('clock')} />
              </div>
              <div className="contact-item__text">
                <span className="contact-item__label">Business Hours</span>
                <span>Mon – Fri: 8:00 AM – 6:00 PM</span>
                <span className="contact-item__sub">Emergency support available 24/7</span>
              </div>
            </div>

            <div className="contact-socials">
              <span className="contact-item__label">Follow Us</span>
              <div className="contact-socials__links">
                <div className="contact-socials__link">
                  <FontAwesomeIcon icon={brands('facebook-f')} />
                </div>
                <div className="contact-socials__link">
                  <FontAwesomeIcon icon={brands('linkedin-in')} />
                </div>
                <div className="contact-socials__link">
                  <FontAwesomeIcon icon={brands('twitter')} />
                </div>
              </div>
            </div>
          </div>

          <div className="contact-us__content__details__form">
            {status === 'success' ? (
              <div className="form-success">
                <div className="form-success__icon">
                  <FontAwesomeIcon icon={solid('circle-check')} />
                </div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. We'll review your message and get back to you within 24 hours.</p>
                <button
                  className="form-success__reset"
                  onClick={() => setStatus(null)}
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-intro">
                  <div className="form-header">
                    <h2>Send A Message</h2>
                    <span className="form-required-note"><Req /> Required</span>
                  </div>
                  <p className="form-subtitle">We'll get back to you within 24 hours.</p>
                </div>

                {status === 'error' && (
                  <div className="form-error">
                    <FontAwesomeIcon icon={solid('triangle-exclamation')} />
                    {' '}Something went wrong — please try again or email us directly at{' '}
                    <strong>business@sterlinglogistics.us</strong>
                  </div>
                )}

                <div className="form-row">
                  <Input
                    title={<>Full Name <Req /></>}
                    required
                    placeholder="John Doe"
                    value={form.name}
                    onChange={handleChange}
                    name="name"
                  />
                  <Input
                    title="Company Name"
                    placeholder="Your Company Ltd."
                    value={form.company}
                    onChange={handleChange}
                    name="company"
                  />
                </div>

                <div className="form-row">
                  <Input
                    title={<>Email Address <Req /></>}
                    type="email"
                    required
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={handleChange}
                    name="email"
                  />
                  <Input
                    title="Phone Number"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={form.phone}
                    onChange={handleChange}
                    name="phone"
                  />
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <div className="label">Inquiry Type <Req /></div>
                    <select
                      name="inquiry_type"
                      value={form.inquiry_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select inquiry type...</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Request a Quote">Request a Quote</option>
                      <option value="Track a Shipment">Track a Shipment</option>
                      <option value="Partnership / Business Proposal">Partnership / Business Proposal</option>
                      <option value="Complaint / Feedback">Complaint / Feedback</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <Input
                    title={<>Country / Region <Req /></>}
                    required
                    placeholder="e.g. United States"
                    value={form.country}
                    onChange={handleChange}
                    name="country"
                  />
                </div>

                <Input
                  title={<>Subject <Req /></>}
                  required
                  placeholder="How can we help you?"
                  value={form.subject}
                  onChange={handleChange}
                  name="subject"
                />

                <div className="msg-field">
                  <InputArea
                    title={<>Message <Req /></>}
                    required
                    rows={5}
                    maxLength={MSG_LIMIT}
                    placeholder="Tell us about your shipment, inquiry, or how we can assist you..."
                    value={form.msg}
                    onChange={handleChange}
                    name="msg"
                  />
                  <div className={`char-counter ${form.msg.length >= MSG_LIMIT ? 'char-counter--limit' : ''}`}>
                    {form.msg.length} / {MSG_LIMIT}
                  </div>
                </div>

                <CustomButton
                  loading={form.loading}
                  type="submit"
                  label="SEND MESSAGE"
                  otherClassName="contact-submit"
                />
              </form>
            )}
          </div>
        </section>

        <UnbeatableSection />
      </div>
    </div>
  )
}

export default ContactUs
